/**
 * cart-integration.js — bridges custom blocks (Bundle Picker, Quantity Breaks,
 * Pair With) to this theme's native cart drawer.
 *
 * The theme (Horizon) has a single cart event: `cart:update` on `document`.
 * `cart-icon-component` reads `detail.data.itemCount` from it to paint the
 * bubble, `cart-items-component` re-renders itself from `detail.data.sections`,
 * and `cart-drawer-component` slides open whenever it sees the event and still
 * carries its `auto-open` attribute.
 *
 * Two things follow from that:
 *
 *   1. `itemCount` must be the cart's TRUE total, not the number of items this
 *      call added. `cart-icon-component` only treats it as a delta when the
 *      source is 'product-form-component'; for everyone else it overwrites the
 *      bubble. /cart/add.js reports only what it just added, so we read the real
 *      total back before firing the event — otherwise adding one product to a
 *      cart of four repaints the bubble as "1".
 *   2. Suppressing the drawer means suppressing `auto-open` for the duration of
 *      the dispatch — the drawer has no "update quietly" input. That is what
 *      `options.silent` does, and it is what lets the Bundle Picker add product
 *      after product without a drawer sliding over the picker each time.
 */
(function () {
  'use strict';
  if (window.themeCartAdd) return;

  function rootUrl() {
    var r = (window.Theme && window.Theme.routes && window.Theme.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  // Collect the section ids the theme needs to re-render (cart drawer + items).
  function sectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      var id = el.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    document.querySelectorAll('cart-drawer-component[data-section-id], [id^="cart-drawer"][data-section-id]').forEach(function (el) {
      var id = el.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function postJSON(path, body) {
    return fetch(rootUrl() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (data) {
        if (!r.ok || (data && data.status)) {
          throw new Error((data && (data.description || data.message)) || 'Could not update the cart');
        }
        return data;
      });
    });
  }

  function getCart() {
    return fetch(rootUrl() + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function makeEvent(response, sourceId, count) {
    var detail = {
      resource: response,
      sourceId: sourceId || 'custom-block',
      data: {
        source: sourceId || 'custom-block',
        itemCount: count,
        sections: response && response.sections
      }
    };
    try {
      return new CustomEvent('cart:update', { bubbles: true, detail: detail });
    } catch (e) {
      var ev = document.createEvent('CustomEvent');
      ev.initCustomEvent('cart:update', true, false, detail);
      return ev;
    }
  }

  /**
   * Dispatch cart:update, optionally without letting the drawer open. The
   * attribute goes back on a macrotask, i.e. after the theme's synchronous
   * listeners have run but before any later add.
   */
  function dispatchCartUpdate(response, sourceId, count, silent) {
    var suppressed = [];
    if (silent) {
      document.querySelectorAll('cart-drawer-component[auto-open]').forEach(function (el) {
        suppressed.push([el, el.getAttribute('auto-open')]);
        el.removeAttribute('auto-open');
      });
    }
    try {
      document.dispatchEvent(makeEvent(response, sourceId, count));
    } finally {
      if (suppressed.length) {
        setTimeout(function () {
          suppressed.forEach(function (pair) {
            pair[0].setAttribute('auto-open', pair[1] == null ? '' : pair[1]);
          });
        }, 0);
      }
    }
    // Keeps the standalone sticky cart bar (cart-bar.js) in step.
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  /**
   * Add one or more line items and notify the theme.
   * @param {Array<{id:number, quantity:number}>} items - quantity is a DELTA
   * @param {string} [sourceId]
   * @param {{silent?: boolean}} [options] - silent: update the cart without opening the drawer
   * @returns {Promise<object>} the /cart/add.js response
   */
  window.themeCartAdd = function (items, sourceId, options) {
    var silent = !!(options && options.silent);
    var body = { items: items };
    var ids = sectionIds();
    if (ids.length) body.sections = ids.join(',');

    return postJSON('cart/add.js', body).then(function (response) {
      // Read the real total back; if that read fails, fall back to the delta
      // rather than dropping the event and leaving the drawer stale.
      return getCart()
        .then(function (cart) { return cart && typeof cart.item_count === 'number' ? cart.item_count : null; })
        .catch(function () { return null; })
        .then(function (total) {
          var count = total;
          if (count == null) {
            try { count = items.reduce(function (s, i) { return s + (i.quantity || 1); }, 0); } catch (e) { count = 0; }
          }
          dispatchCartUpdate(response, sourceId, count, silent);
          return response;
        });
    });
  };

  /**
   * Set the absolute quantity of an existing cart line and notify the theme.
   * /cart/change.js returns the whole cart, so no follow-up read is needed.
   * @param {number} line - 1-based line index
   * @param {number} quantity - absolute quantity for that line (0 removes it)
   * @param {string} [sourceId]
   * @param {{silent?: boolean}} [options]
   * @returns {Promise<object>} the resulting cart
   */
  window.themeCartChange = function (line, quantity, sourceId, options) {
    var silent = !!(options && options.silent);
    var body = { line: line, quantity: quantity };
    var ids = sectionIds();
    if (ids.length) body.sections = ids.join(',');

    return postJSON('cart/change.js', body).then(function (cart) {
      dispatchCartUpdate(cart, sourceId, cart && cart.item_count, silent);
      return cart;
    });
  };

  /** Read the current cart. Exposed so blocks don't each re-implement it. */
  window.themeCartGet = getCart;
})();
